import type { TranslationKey } from "./i18n";

type Translate = (k: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Map a login / registration error to a localized, user-facing message.
 *
 * The gateway returns English validation/credential strings — e.g.
 * "invalid credentials", "password: String should have at least 8 characters",
 * "email: value is not a valid email address: …". We surface their Russian
 * equivalents and fall back to a localized generic (never the raw English) so
 * the whole auth flow stays in one language.
 */
export function humanizeAuthError(
  err: unknown,
  t: Translate,
  fallbackKey: TranslationKey,
): string {
  const status =
    typeof err === "object" && err !== null && "status" in err
      ? Number((err as { status: unknown }).status)
      : 0;
  const raw = err instanceof Error ? err.message : "";
  const lower = raw.toLowerCase();

  if (
    status === 401 ||
    lower.includes("invalid credentials") ||
    lower.includes("incorrect password")
  ) {
    return t("auth.errInvalidCredentials");
  }
  if (
    lower.includes("already") &&
    (lower.includes("exist") || lower.includes("registered") || lower.includes("taken"))
  ) {
    return t("auth.errEmailTaken");
  }
  if (lower.includes("email") && (lower.includes("not a valid") || lower.includes("invalid email"))) {
    return t("auth.invalidEmail");
  }
  if (lower.includes("password") && lower.includes("at least") && lower.includes("character")) {
    return t("auth.passwordWeak");
  }
  return t(fallbackKey);
}
